import { logSecurityEvent } from "./observability";

export const TURNSTILE_TOKEN_FIELD = "cf-turnstile-response";

const VERIFY_ENDPOINT =
	"https://challenges.cloudflare.com/turnstile/v0/siteverify";

// Cloudflare-published always-pass test keys. Documented at
// https://developers.cloudflare.com/turnstile/troubleshooting/testing/.
// We use these when no real keys are configured (local dev / CI) so the widget
// still mounts and the verifier short-circuits to "ok" instead of breaking the form.
/** Always-pass site key (visible widget). */
export const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";
/** Always-pass secret key. */
export const TURNSTILE_TEST_SECRET = "1x0000000000000000000000000000000AA";

export type TurnstileVerifyOptions = {
	token: string | null | undefined;
	secret: string | undefined;
	remoteIp?: string | null;
	expectedAction?: string;
	/** Override fetch — handy for tests. Defaults to global fetch. */
	fetchImpl?: typeof fetch;
};

export type TurnstileVerifyResult =
	| { ok: true; bypassed?: boolean }
	| { ok: false; error: string };

type SiteverifyResponse = {
	success: boolean;
	"error-codes"?: string[];
	action?: string;
	hostname?: string;
	cdata?: string;
};

/**
 * Verifies a Turnstile token against Cloudflare's siteverify API.
 *
 * Behaviour:
 *  - Missing secret → returns `{ ok: true, bypassed: true }` and logs a warning.
 *    Turnstile becomes a no-op rather than locking out every visitor when env
 *    isn't wired up. Production CI sets the secret, so this branch only fires
 *    in local dev or accidental misconfig.
 *  - Missing token → `{ ok: false, error: "missing_token" }`.
 *  - Network/parse failure → `{ ok: false, error: "verify_failed" }`.
 *  - Unsuccessful verify → `{ ok: false, error: <first error code> }`.
 *  - `expectedAction` mismatch → `{ ok: false, error: "action_mismatch" }`.
 */
export async function verifyTurnstileToken(
	options: TurnstileVerifyOptions,
): Promise<TurnstileVerifyResult> {
	const { token, secret, remoteIp, expectedAction, fetchImpl } = options;

	if (!secret) {
		logSecurityEvent("turnstile_secret_missing");
		return { ok: true, bypassed: true };
	}

	if (!token || typeof token !== "string" || token.length === 0) {
		return { ok: false, error: "missing_token" };
	}

	const body = new FormData();
	body.append("secret", secret);
	body.append("response", token);
	if (remoteIp) {
		body.append("remoteip", remoteIp);
	}

	let data: SiteverifyResponse;
	try {
		const doFetch = fetchImpl ?? fetch;
		const response = await doFetch(VERIFY_ENDPOINT, {
			method: "POST",
			body,
		});
		if (!response.ok) {
			logSecurityEvent("turnstile_verify_http_error", {
				status: response.status,
			});
			return { ok: false, error: "verify_failed" };
		}
		data = (await response.json()) as SiteverifyResponse;
	} catch (err) {
		logSecurityEvent("turnstile_verify_network_error", {
			message: err instanceof Error ? err.message : String(err),
		});
		return { ok: false, error: "verify_failed" };
	}

	if (!data.success) {
		const code = data["error-codes"]?.[0] ?? "verify_failed";
		logSecurityEvent("turnstile_verify_rejected", {
			codes: data["error-codes"] ?? [],
		});
		return { ok: false, error: code };
	}

	if (expectedAction && data.action && data.action !== expectedAction) {
		logSecurityEvent("turnstile_action_mismatch", {
			expected: expectedAction,
			received: data.action,
		});
		return { ok: false, error: "action_mismatch" };
	}

	return { ok: true };
}

/**
 * Pulls the Turnstile token from a `FormData` payload submitted by the widget.
 * The official client sets `cf-turnstile-response`. We accept that plus a
 * fallback `turnstileToken` in case a server action posts it under a different
 * name (e.g. fetched manually via the JS API).
 */
export function getTurnstileToken(formData: FormData): string | null {
	const primary = formData.get(TURNSTILE_TOKEN_FIELD);
	if (typeof primary === "string" && primary.length > 0) return primary;
	const alt = formData.get("turnstileToken");
	if (typeof alt === "string" && alt.length > 0) return alt;
	return null;
}
